"use client";

import { customImageLoader } from "@/lib/utils/customLoader";
import { observeLozad } from "@/lib/utils/lozadObserver";
import Image, { ImageProps } from "next/image";
import { type CSSProperties, type FC, useEffect, useState } from "react";
import { preload } from "react-dom";

// Inline 1x1 transparent gif — no network request, unlike the old shared
// placeholder image, so the CSS skeleton below is the only thing visible
// until lozad swaps in the real image.
const TRANSPARENT_PIXEL =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

interface ImageWrapperProps
    extends Omit<ImageProps, "src" | "alt" | "loader"> {
    src?: string | null | undefined;
    alt?: string;
    fallbackSrc?: string;
    cdnBaseUrl?: string;
    unoptimized?: boolean;
    priority?: boolean;
    isMobile?: boolean; // caps priority images to a single 1x request
    sourceWidth?: number; // CDN request width in `fill` mode
    sourceHeight?: number; // CDN request height in `fill` mode
}

const MOBILE_PRIORITY_WIDTH = 400;

const DEFAULT_FALLBACK = `${process.env.NEXT_PUBLIC_CDN_URL}placeholder-car.webp`;
const DEFAULT_CDN_BASE = process.env.NEXT_PUBLIC_CDN_URL;

const ImageWrapper: FC<ImageWrapperProps> = ({
    src,
    alt = "Image",
    fallbackSrc = DEFAULT_FALLBACK,
    cdnBaseUrl = DEFAULT_CDN_BASE,
    unoptimized = true,
    priority = false,
    isMobile = false,
    sourceWidth,
    sourceHeight,
    className,
    width,
    height,
    fill,
    sizes,
    quality,
    placeholder,
    blurDataURL,
    onLoadingComplete,
    overrideSrc,
    loading: _loading,
    style,
    ...rest
}) => {

    let finalSrc = fallbackSrc;

    // On mobile, priority images request one fixed 1x-ish width instead of
    // next/image's usual 2x/3x srcset — smaller download, softer on Retina.
    const cdnWidth = width ?? sourceWidth ?? (priority && isMobile ? MOBILE_PRIORITY_WIDTH : undefined);
    const cdnHeight = height ?? sourceHeight;

    // CDN crops to width+height exactly, so a fixed height would distort
    // next/image's multiple width variants — only bake it in when unoptimized.
    const includeCdnHeight = unoptimized;

    if (src && typeof src === "string" && src.trim()) {
        const trimmed = src.trim();

        if (/^https?:\/\//i.test(trimmed)) {
            try {
                const u = new URL(trimmed);
                if (cdnWidth) u.searchParams.set("width", String(cdnWidth));
                if (includeCdnHeight && cdnHeight) u.searchParams.set("height", String(cdnHeight));
                else u.searchParams.delete("height");
                finalSrc = u.toString();
            } catch {
                finalSrc = trimmed;
            }
        } else {
            const heightParam = includeCdnHeight ? `&height=${cdnHeight || 800}` : "";
            finalSrc = `${cdnBaseUrl}${trimmed.replace(/^\/+/, "")}?width=${cdnWidth || 1200}${heightParam}`;
        }
    }

    const isFillMode = fill || !(width && height);
    // Mirrors next/image's own `fill` positioning — inline, so it never
    // collides with whatever sizing classes the caller's className brings.
    const mergedStyle: CSSProperties | undefined = isFillMode
        ? { position: "absolute", inset: 0, width: "100%", height: "100%", ...style }
        : style;

    // Start/refresh the shared lozad observer so this image gets watched.
    useEffect(() => {
        if (!priority) observeLozad();
    }, [priority]);

    // Tracks the skeleton — cleared once the real image (not the transparent
    // placeholder pixel) finishes loading.
    const [isLoaded, setIsLoaded] = useState(false);

    if (priority && isMobile) {
        // This raw <img> bypasses next/image, so it also misses out on the
        // <link rel="preload"> next/image's own priority path injects
        // automatically — add it manually so the LCP image starts
        // downloading immediately instead of waiting for the HTML parser.
        preload(finalSrc, { as: "image", fetchPriority: "high" });
        // Bypass next/image's density-based srcset — always the one fixed width.
        return (
            <img
                src={finalSrc}
                alt={alt}
                fetchPriority="high"
                loading="eager"
                decoding="async"
                className={className}
                style={mergedStyle}
                {...(isFillMode ? {} : { width, height })}
                {...rest}
            />
        );
    }

    if (priority) {
        return (
            <Image
                src={finalSrc}
                alt={alt}
                unoptimized={unoptimized}
                fetchPriority="high"
                loading="eager"
                loader={customImageLoader}
                className={className}
                quality={quality}
                placeholder={placeholder}
                blurDataURL={blurDataURL}
                onLoadingComplete={onLoadingComplete}
                overrideSrc={overrideSrc}
                style={style}
                {...(isFillMode
                    ? { fill: true, sizes: sizes ?? "100vw" }
                    : { width, height, sizes })}
                {...rest}
            />
        );
    }

    // Real URL goes in data-src; lozad swaps it into src on scroll.
    // Until then, src stays on a transparent pixel and the CSS skeleton
    // (animate-pulse + gray background) fills the box instead.
    return (
        <img
            src={TRANSPARENT_PIXEL}
            data-src={finalSrc}
            alt={alt}
            onLoad={(e) => {
                if (e.currentTarget.src !== TRANSPARENT_PIXEL) setIsLoaded(true);
            }}
            className={[
                "lozad",
                !isLoaded && "bg-gray-200",
                className ?? "",
            ].filter(Boolean).join(" ")}
            style={mergedStyle}
            {...(isFillMode ? {} : { width, height })}
            {...rest}
        />
    );
};

export default ImageWrapper;